import type {
  MessageAppend,
  TransactionButtonProps,
  TransactionContainerProps,
} from '@bitte-ai/types';
import { Transaction as SuiTransaction } from '@mysten/sui/transactions';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTransaction } from '../../../hooks/useTransaction';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { createDeclineTransactionMessage } from '../../../lib/transaction-messages';
import { useAccount } from '../../../state/wallet.state';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../ui/accordion';
import { Button } from '../../ui/button';
import { CardFooter, CardHeader } from '../../ui/card';
import { CopyStandard } from '../CopyStandard';
import DefaultTxApproveButton from '../default-components/DefaultTxApproveButton';
import DefaultTxContainer from '../default-components/DefaultTxContainer';
import DefaultTxDeclineButton from '../default-components/DefaultTxDeclineButton';
import LoadingMessage from '../loading/LoadingMessage';
import { TransactionResult } from './TransactionResult';

//TODO LOADING STATE after tx successfully submitted
export const SuiTxCard = ({
  suiTransactionBytes,
  messageBackgroundColor,
  borderColor,
  textColor,
  customTxContainer: TxContainer = DefaultTxContainer,
  customApproveTxButton: ApproveButton = DefaultTxApproveButton,
  customDeclineTxButton: DeclineButton = DefaultTxDeclineButton,
  append,
  messageId,
}: {
  suiTransactionBytes?: string;
  messageBackgroundColor: string;
  borderColor: string;
  textColor: string;
  customTxContainer?: React.ComponentType<TransactionContainerProps>;
  customApproveTxButton?: React.ComponentType<TransactionButtonProps>;
  customDeclineTxButton?: React.ComponentType<TransactionButtonProps>;
  append: MessageAppend;
  messageId: string;
}) => {
  const { width } = useWindowSize();
  const isMobile = !!width && width < 640;
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  const [txDigest, setTxDigest] = useState<string | undefined>();
  const { suiWallet, suiAddress } = useAccount();
  const messageAppended = useRef(false);
  const [transaction, setTransaction] = useState<SuiTransaction | null>(null);
  const [transactionData, setTransactionData] = useState<any>(null);

  const { handleTxn } = useTransaction({ suiWallet });

  useEffect(() => {
    if (suiTransactionBytes && !transaction) {
      try {
        const bytes = Buffer.from(suiTransactionBytes, 'base64');
        const txn = SuiTransaction.from(bytes);
        setTransaction(txn);

        // Just get the raw data without parsing
        try {
          const rawData = txn.getData();
          setTransactionData(rawData);
        } catch (error) {
          console.error('Error extracting transaction data:', error);
          setTransactionData({ error: 'Failed to extract transaction data' });
        }
      } catch (error) {
        console.error('Failed to parse Sui transaction bytes:', error);
        setErrorMsg('Failed to parse transaction data');
      }
    }
  }, [suiTransactionBytes, transaction]);

  if (!suiTransactionBytes || !transaction || !transactionData) {
    return (
      <p className="bitte-my-4 bitte-overflow-auto bitte-text-center">
        Unable to create Sui transaction.
      </p>
    );
  }

  const handleSmartAction = async () => {
    setIsLoading(true);
    try {
      const result = await handleTxn({
        suiTransaction: transaction,
      });

      if (result?.sui?.digest) {
        setTxDigest(result.sui.digest);
        setIsLoading(false);
        if (!messageAppended.current) {
          messageAppended.current = true;
          await append({
            id: messageId,
            role: 'assistant',
            content: `Transaction successfully submitted. Transaction digest: ${result.sui.digest}\nView on SuiVision: https://suivision.xyz/txblock/${result.sui.digest}`,
            createdAt: new Date(),
          });
        }
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMsg(
        error instanceof Error
          ? error.message
          : `Unknown error: ${JSON.stringify(error)}`,
      );

      if (!messageAppended.current) {
        messageAppended.current = true;
        await append({
          id: messageId,
          role: 'assistant',
          content: `Transaction failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          createdAt: new Date(),
        });
      }
    }
  };

  const declineTransaction = async () => {
    setIsDeclined(true);
    await append(createDeclineTransactionMessage(messageId));
    setIsLoading(false);
  };

  return (
    <TxContainer
      style={{
        backgroundColor: messageBackgroundColor,
        borderColor,
        textColor,
      }}
    >
      <CardHeader
        className="bitte-border-b bitte-p-4 bitte-text-center bitte-md:p-6"
        style={{ borderColor: borderColor }}
      >
        <p className="bitte-text-xl bitte-font-semibold">Sui Transaction</p>
      </CardHeader>

      <div className="bitte-p-6">
        <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
          <Accordion type="single" collapsible defaultValue="txData">
            <AccordionItem value="txData">
              <AccordionTrigger className="bitte-pt-0">
                <div className="bitte-flex bitte-items-center bitte-justify-between bitte-text-sm">
                  <p>Transaction Details</p>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <pre className="bitte-whitespace-pre-wrap bitte-overflow-auto bitte-max-h-60 bitte-text-xs">
                  {JSON.stringify(transactionData, null, 2)}
                </pre>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {suiAddress && (
            <div className="bitte-flex bitte-items-center bitte-justify-between">
              <span>Sender:</span>
              <CopyStandard
                text={suiAddress}
                textSize="sm"
                charSize={isMobile ? 7 : 12}
              />
            </div>
          )}
        </div>
      </div>

      {errorMsg ? (
        <div className="bitte-flex bitte-flex-col bitte-items-center bitte-gap-4 bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm">
          <p className="bitte-text-red-300">
            An error occurred trying to execute your transaction: {errorMsg}.
          </p>
          <Button
            className="bitte-w-1/2"
            variant="outline"
            onClick={() => {
              setErrorMsg('');
            }}
          >
            Dismiss
          </Button>
        </div>
      ) : isLoading ? (
        <LoadingMessage color={textColor} />
      ) : null}

      {isDeclined ? (
        <div className="bitte-p-6 bitte-text-center">
          <X className="bitte-mx-auto bitte-mb-2 bitte-h-8 bitte-w-8 bitte-text-red-500" />
          <p>Transaction declined</p>
        </div>
      ) : null}

      {txDigest ? (
        <div className="bitte-p-6">
          <TransactionResult
            result={{ sui: { digest: txDigest } }}
            accountId={suiAddress || ''}
            textColor={textColor}
          />
          <div className="bitte-mt-2 bitte-text-center">
            <a
              href={`https://suivision.xyz/txblock/${txDigest}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bitte-text-blue-500 hover:bitte-text-blue-700 bitte-underline"
            >
              View on SuiVision
            </a>
          </div>
        </div>
      ) : null}

      {!isLoading && !errorMsg && !txDigest && !isDeclined && (
        <CardFooter className="bitte-flex bitte-items-center bitte-gap-6">
          <>
            <DeclineButton onClick={declineTransaction} disabled={isLoading} />
            <ApproveButton
              onClick={handleSmartAction}
              disabled={isLoading || !suiWallet?.connected}
              isLoading={isLoading}
            />
          </>
        </CardFooter>
      )}
    </TxContainer>
  );
};
